/**
 * The economic roles a contract ledger entry's amount can concern. CUSTOMER and SELLER
 * reference WarrantySphere's own Customer/Distributor records; INSURER, AGENT, ADMINISTRATOR,
 * PLATFORM and OTHER may reference a tenant-configured PartyRecord when a rating-matrix fee was
 * attributed to one, and are otherwise tracked by name only.
 */
export enum PartyType {
  /** The end consumer who bought the contract. */
  CUSTOMER = 'CUSTOMER',
  /** The distributor who sold the contract — WarrantySphere's existing Distributor record. */
  SELLER = 'SELLER',
  /** The underwriter/carrier backing the contract's risk. */
  INSURER = 'INSURER',
  /** An agent/producer owed part of the contract's price (e.g. an agent commission). */
  AGENT = 'AGENT',
  /** The third-party administrator — the tenant itself, in most programs — administering the contract. */
  ADMINISTRATOR = 'ADMINISTRATOR',
  /** The WarrantySphere platform, when a program's economics include a platform-level fee. */
  PLATFORM = 'PLATFORM',
  /**
   * The state/county/local government a jurisdictional sales or premium tax is owed to —
   * distinct from INSURER even though an insurer often remits it, because a cancellation's
   * prorated tax credit is tracked back to the taxing jurisdiction, not folded into the
   * insurer's own premium clawback (see buildContractComponentSnapshot's tax loop). Multiple
   * jurisdictions on one contract (e.g. state and county) share this one type — each stays
   * separately itemized at the Component level (one row per jurisdiction, per its own label),
   * but rolls up into a single combined TAX_AUTHORITY line at the Allocation/derived-balance
   * level, the same way any other party's multiple components already roll up into one line.
   */
  TAX_AUTHORITY = 'TAX_AUTHORITY',
  /** Any counterparty not covered by the roles above. */
  OTHER = 'OTHER',
}

/**
 * Identifies who a ledger entry's amount is owed to, received from, or paid to — the
 * diagram's "payee" / "receivedFrom" / "paidTo" columns, generalized into one shape reused
 * across components, allocations, funding, and disbursements.
 *
 * A snapshot, not a live relation: `partyName` is captured at entry time so the entry stays
 * readable even if the referenced Customer/Distributor is later renamed or removed.
 */
export interface Party {
  readonly type: PartyType;

  /**
   * Customer.Model id when `type` is CUSTOMER, Distributor.Model id when `type` is SELLER (sellers
   * are always Distributors, never PartyRecords). For any other role, the PartyRecord id when the amount was attributed to
   * one; null when WarrantySphere holds no record for the counterparty.
   */
  readonly partyId: string | null;

  readonly partyName: string | null;
}

const PARTY_TYPE_LABELS: Record<PartyType, string> = {
  [PartyType.CUSTOMER]: 'Customer',
  [PartyType.SELLER]: 'Seller',
  [PartyType.INSURER]: 'Insurer',
  [PartyType.AGENT]: 'Agent',
  [PartyType.ADMINISTRATOR]: 'Administrator',
  [PartyType.PLATFORM]: 'Platform',
  [PartyType.TAX_AUTHORITY]: 'Tax authority',
  [PartyType.OTHER]: 'Other',
};

/** The user-facing name of a party role, e.g. TAX_AUTHORITY → "Tax authority". */
export function partyTypeLabel(type: PartyType): string {
  return PARTY_TYPE_LABELS[type] ?? type;
}
